import api, { apiClient } from './api';
import {
  AccountingAccount,
  ExpenseCategory,
  Expense,
  IncomeEntry,
  Pagination,
  DashboardSummary,
  ProfitLossReport,
  ExpenseBreakdownRow,
  AccountsReceivableReport,
  CashFlowReport
} from '../types/accounting';

const buildQueryParams = (params: Record<string, any>): string => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, String(value));
    }
  });
  return queryParams.toString();
};

export const accountingApi = {
  // Accounts
  getAccounts: (includeInactive = false) =>
    apiClient.get<{ accounts: AccountingAccount[] }>(
      `/accounting/accounts${includeInactive ? '?includeInactive=true' : ''}`
    ),

  createAccount: (data: { name: string; accountType: 'bank' | 'cash'; openingBalance?: number; notes?: string }) =>
    apiClient.post<{ id: number }>('/accounting/accounts', data),

  updateAccount: (id: number, data: Partial<{ name: string; accountType: 'bank' | 'cash'; openingBalance: number; notes: string; isActive: boolean }>) =>
    apiClient.put<null>(`/accounting/accounts/${id}`, data),

  deactivateAccount: (id: number) =>
    apiClient.delete<null>(`/accounting/accounts/${id}`),

  // Expense categories
  getExpenseCategories: (includeInactive = false) =>
    apiClient.get<{ categories: ExpenseCategory[] }>(
      `/accounting/expense-categories${includeInactive ? '?includeInactive=true' : ''}`
    ),

  createExpenseCategory: (name: string) =>
    apiClient.post<{ id: number }>('/accounting/expense-categories', { name }),

  updateExpenseCategory: (id: number, data: Partial<{ name: string; isActive: boolean }>) =>
    apiClient.put<null>(`/accounting/expense-categories/${id}`, data),

  // Expenses
  getExpenses: (filters: { startDate?: string; endDate?: string; categoryId?: number; accountId?: number; page?: number; limit?: number } = {}) =>
    apiClient.get<{ expenses: Expense[]; pagination: Pagination }>(
      `/accounting/expenses?${buildQueryParams(filters)}`
    ),

  createExpense: (data: { categoryId: number; accountId: number; payee?: string; amount: number; expenseDate: string; description?: string }) =>
    apiClient.post<{ id: number }>('/accounting/expenses', data),

  updateExpense: (id: number, data: Partial<{ categoryId: number; accountId: number; payee: string; amount: number; expenseDate: string; description: string }>) =>
    apiClient.put<null>(`/accounting/expenses/${id}`, data),

  deleteExpense: (id: number) =>
    apiClient.delete<null>(`/accounting/expenses/${id}`),

  // Manual income entries
  getIncomeEntries: (filters: { startDate?: string; endDate?: string; accountId?: number; page?: number; limit?: number } = {}) =>
    apiClient.get<{ income: IncomeEntry[]; pagination: Pagination }>(
      `/accounting/income?${buildQueryParams(filters)}`
    ),

  createIncomeEntry: (data: { accountId: number; amount: number; incomeDate: string; source?: string; description?: string }) =>
    apiClient.post<{ id: number }>('/accounting/income', data),

  updateIncomeEntry: (id: number, data: Partial<{ accountId: number; amount: number; incomeDate: string; source: string; description: string }>) =>
    apiClient.put<null>(`/accounting/income/${id}`, data),

  deleteIncomeEntry: (id: number) =>
    apiClient.delete<null>(`/accounting/income/${id}`),

  // Dashboard & reports
  getDashboard: () =>
    apiClient.get<DashboardSummary>('/accounting/dashboard'),

  getProfitLoss: (startDate?: string, endDate?: string) =>
    apiClient.get<ProfitLossReport>(`/accounting/reports/profit-loss?${buildQueryParams({ startDate, endDate })}`),

  getExpenseBreakdown: (startDate?: string, endDate?: string) =>
    apiClient.get<{ breakdown: ExpenseBreakdownRow[] }>(`/accounting/reports/expense-breakdown?${buildQueryParams({ startDate, endDate })}`),

  getAccountsReceivable: () =>
    apiClient.get<AccountsReceivableReport>('/accounting/reports/accounts-receivable'),

  getCashFlow: (startDate?: string, endDate?: string) =>
    apiClient.get<CashFlowReport>(`/accounting/reports/cash-flow?${buildQueryParams({ startDate, endDate })}`),

  // Downloads a PDF of one report, or of all reports when report = 'all'
  downloadReportPdf: async (report: 'all' | 'pl' | 'breakdown' | 'ar' | 'cashflow', startDate?: string, endDate?: string) => {
    const response = await api.get(
      `/accounting/reports/pdf?${buildQueryParams({ report, startDate, endDate })}`,
      { responseType: 'blob', timeout: 120000 }
    );

    const contentDisposition = response.headers['content-disposition'];
    let filename = `accounting-${report}.pdf`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+?)"?$/);
      if (match) filename = match[1];
    }

    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};
